import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    Typography,
    IconButton,
    Input,
    Stack,
    Textarea,
    Divider,
    Accordion,
    AccordionGroup,
    AccordionSummary,
    AccordionDetails,
} from '@mui/joy';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

/**
 * FlowBuilder component for editing Compliance V2 flows
 * Structure:
 * {
 *   steps: [{ name: string, id: string, config: object, type: string }],
 *   config: object
 * }
 */
const FlowBuilder = ({ value, onChange }) => {
    const [flow, setFlow] = useState({ steps: [], config: {} });

    useEffect(() => {
        if (value) {
            // Ensure we have the basic structure
            setFlow({
                steps: Array.isArray(value.steps) ? value.steps : [],
                config: value.config || {}
            });
        }
    }, [value]);

    const updateFlow = (newFlow) => {
        setFlow(newFlow);
        onChange(newFlow);
    };

    const handleAddStep = () => {
        const newStep = {
            id: `step_${Date.now()}`,
            name: 'New Step',
            type: 'generic',
            config: {}
        };
        updateFlow({
            ...flow,
            steps: [...flow.steps, newStep]
        });
    };

    const handleDeleteStep = (index) => {
        const newSteps = flow.steps.filter((_, i) => i !== index);
        updateFlow({
            ...flow,
            steps: newSteps
        });
    };

    const handleStepChange = (index, field, newValue) => {
        const newSteps = [...flow.steps];
        newSteps[index] = {
            ...newSteps[index],
            [field]: newValue
        };
        updateFlow({
            ...flow,
            steps: newSteps
        });
    };

    const handleConfigChange = (newValue) => {
        try {
            const parsedConfig = JSON.parse(newValue);
            updateFlow({
                ...flow,
                config: parsedConfig
            });
        } catch (e) {
            // Just update local state if invalid JSON, but we might want to validation
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>

            {/* Global Config Section */}
            <Card variant="outlined">
                <Typography level="title-md" sx={{ mb: 1 }}>Global Flow Configuration</Typography>
                <Textarea
                    minRows={3}
                    value={JSON.stringify(flow.config, null, 2)}
                    onChange={(e) => handleConfigChange(e.target.value)}
                    placeholder="{}"
                    sx={{ fontFamily: 'monospace' }}
                />
            </Card>

            <Divider />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography level="title-lg">Flow Steps</Typography>
                <Button startDecorator={<AddIcon />} onClick={handleAddStep}>
                    Add Step
                </Button>
            </Box>

            <Box sx={{ overflowY: 'auto', flex: 1, pr: 1 }}>
                <AccordionGroup sx={{ maxWidth: '100%' }}>
                    {flow.steps.map((step, index) => (
                        <Accordion key={index} defaultExpanded>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                slotProps={{
                                    button: {
                                        sx: {
                                            borderRadius: 'md',
                                            '&:hover': { bgcolor: 'background.level1' },
                                        }
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                                    <DragIndicatorIcon sx={{ color: 'neutral.400' }} />
                                    <Typography level="title-sm" sx={{ minWidth: 20 }}>{index + 1}.</Typography>
                                    <Input
                                        size="sm"
                                        value={step.name}
                                        onChange={(e) => handleStepChange(index, 'name', e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder="Step Name"
                                        sx={{ flex: 1 }}
                                    />
                                    <Input
                                        size="sm"
                                        value={step.type}
                                        onChange={(e) => handleStepChange(index, 'type', e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder="Type"
                                        sx={{ width: 120 }}
                                    />
                                    <IconButton
                                        size="sm"
                                        color="danger"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteStep(index);
                                        }}
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={1} sx={{ p: 1 }}>
                                    <Typography level="body-sm">Configuration (JSON)</Typography>
                                    <Textarea
                                        minRows={4}
                                        value={JSON.stringify(step.config, null, 2)}
                                        onChange={(e) => {
                                            try {
                                                handleStepChange(index, 'config', JSON.parse(e.target.value));
                                            } catch (error) {
                                                // Handle invalid JSON input gracefully?
                                            }
                                        }}
                                        sx={{ fontFamily: 'monospace', fontSize: 'sm' }}
                                    />
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </AccordionGroup>

                {flow.steps.length === 0 && (
                    <Box sx={{ p: 4, textAlign: 'center', color: 'neutral.500', border: '1px dashed #ccc', borderRadius: 'md' }}>
                        <Typography>No steps defined. Add a step to get started.</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default FlowBuilder;
